import React, { useEffect, useState } from "react";
import { useStore } from "../store";
import api from "../api";
import {
  Building2, Send, QrCode, BarChart3, Code, Package, UsersRound, Landmark,
  FileText, Copy, Check, Plus, TrendingUp, Download, AlertCircle, Webhook,
  Shield, Trash2, Eye, EyeOff, ExternalLink, Banknote,
} from "lucide-react";
import QRCode from "qrcode";
import { generateReceiptPDF } from "./pdfReceipt";
import AntiSanctionSidebar from "./AntiSanctionSidebar";
import { exportReportCSV, exportReportPDF } from "./reportExport";
import PDFExportModal from "./PDFExportModal";
import ReportExportModal from "./ReportExportModal";
import { useT } from "../site/i18n";

/* ═══════════════════ helpers ═══════════════════ */
const Page = ({ title, sub, children, testId }) => (
  <div data-testid={testId}>
    <h2 className="page-title">{title}</h2>
    {sub && <p className="page-sub">{sub}</p>}
    {children}
  </div>
);
const Card = ({ children, style = {}, testId, ...p }) => (
  <div className="tg-card" data-testid={testId} style={{ padding: 22, ...style }} {...p}>{children}</div>
);
const fmt = (n, d = 2) => Number(n || 0).toFixed(d);

/* ═══════════════════ 1. BUSINESS OVERVIEW ═══════════════════ */
export function BizOverviewPage() {
  const { t } = useT();
  const { user } = useStore();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/business/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const kpi = [
    { l: t("business.84dc1b"),   v: stats ? `€${fmt(stats.monthRevenue)}` : "—", ic: <TrendingUp size={18} />, c: "#00F2FF" },
    { l: t("business.a50e0a"), v: stats?.todayTxCount ?? "—",                 ic: <BarChart3 size={18} />, c: "#10F4A7" },
    { l: t("business.fb07a0"),       v: stats ? `€${fmt(stats.avgTicket)}` : "—",   ic: <Banknote size={18} />,  c: "#FFD700" },
    { l: t("pages.400a03"),          v: stats ? `${fmt(stats.commissionRate, 2)}%` : "—", ic: <QrCode size={18} />, c: "#BC13FE" },
  ];

  return (
    <Page title="🏢 Business Overview" sub={t("business.6189a9")} testId="biz-overview-page">
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        {kpi.map((k, i) => (
          <Card key={i} style={{ background: `linear-gradient(135deg, ${k.c}10, rgba(255,255,255,0.02))`, borderColor: `${k.c}33` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: k.c }}>{k.ic}<span style={{ fontSize: 11, opacity: 0.8, textTransform: "uppercase", letterSpacing: 1 }}>{k.l}</span></div>
            <div style={{ fontSize: 28, fontWeight: 900, marginTop: 10, fontFeatureSettings: '"tnum"' }}>{k.v}</div>
          </Card>
        ))}
      </div>

      {/* Chart (SVG sparkline) */}
      <Card style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{t("business.1a9239")}</h3>
          <span style={{ fontSize: 11, color: "var(--tg-text-dim)" }}>{t("business.25429b")}</span>
        </div>
        <Sparkline data={stats?.days30 || []} />
      </Card>

      {/* Recent transactions table */}
      <Card>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.8f4494")}</h3>
        <table className="dash-table">
          <thead><tr><th>{t("business.e90dde")}</th><th>{t("business.4af22f")}</th><th>{t("pages3.cf59eb")}</th><th>TGC</th><th>Hash</th></tr></thead>
          <tbody>
            {(stats?.recentPayments || []).map((t, i) => (
              <tr key={i}>
                <td style={{ color: "var(--tg-text-dim)" }}>{t.at}</td>
                <td>{t.customer}</td>
                <td style={{ fontWeight: 700, color: "#10F4A7" }}>€{fmt(t.amount)}</td>
                <td style={{ color: "#FFD700" }}>{t.tgc}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11, color: "var(--tg-blue)" }}>{t.hash}</td>
              </tr>
            ))}
            {!stats && <tr><td colSpan={5} style={{ color: "var(--tg-text-hint)", textAlign: "center" }}>{t("user.89d69a")}</td></tr>}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

function Sparkline({ data }) {
  const { t } = useT();
  if (!data.length) return <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--tg-text-hint)", fontSize: 12 }}>{t("business.dee9a2")}</div>;
  const max = Math.max(...data.map((d) => d.revenue || d.v || 0), 1);
  const w = 800, h = 120;
  const pts = data.map((d, i) => `${(i / (data.length - 1)) * w},${h - ((d.revenue || d.v || 0) / max) * h * 0.9 - 5}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 140 }}>
      <defs>
        <linearGradient id="bizGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00F2FF" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#00F2FF" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline fill="url(#bizGrad)" stroke="none" points={`0,${h} ${pts} ${w},${h}`} />
      <polyline fill="none" stroke="#00F2FF" strokeWidth="2.5" points={pts} />
    </svg>
  );
}

/* ═══════════════════ 2. ACQUIRING (QR + Terminal) ═══════════════════ */
export function AcquiringPage() {
  const { t } = useT();
  const [amount, setAmount] = useState("");
  const [terminal, setTerminal] = useState("");
  const [qr, setQr] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [newTerm, setNewTerm] = useState({ name: "", location: "", type: "QR + NFC" });

  const loadTerminals = async () => {
    try { const r = await api.get("/biz/terminals"); setTerminals(r.data.terminals || []); } catch (e) {}
  };
  useEffect(() => { loadTerminals(); }, []);

  const addTerminal = async () => {
    if (!newTerm.name) return;
    try {
      await api.post("/biz/terminals", newTerm);
      setNewTerm({ name: "", location: "", type: "QR + NFC" });
      loadTerminals();
    } catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const delTerminal = async (id) => {
    if (!window.confirm(`${t("business.confirm-delete")} ${id}?`)) return;
    try { await api.delete(`/biz/terminals/${id}`); loadTerminals(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };

  const gen = async () => {
    if (!amount || Number(amount) <= 0) return;
    const id = "TGC-" + Math.random().toString(36).slice(2, 10).toUpperCase();
    const payload = JSON.stringify({
      tgc_invoice: id,
      amount: Number(amount),
      currency: "EUR",
      terminal: terminal || "Main POS",
      ts: Date.now(),
    });
    const dataUrl = await QRCode.toDataURL(payload, { width: 360, margin: 1, color: { dark: "#050505", light: "#FFFFFFFF" } });
    setQr({ id, amount, terminal: terminal || "Main POS", created: new Date().toLocaleString("ru-RU"), payload, dataUrl });
  };

  const downloadPDF = (lang, currency) => generateReceiptPDF({
    type: "ACQUIRING",
    reference: qr.id,
    rows: [
      ["Invoice ID",     qr.id],
      ["Amount",         `€${qr.amount}`],
      ["Currency",       "EUR"],
      ["Terminal / POS", qr.terminal],
      ["Fee (merchant)", "0.5%"],
      ["Created at",     qr.created],
      ["Status",         "Awaiting payment"],
    ],
    qrDataUrl: qr.dataUrl,
    lang, currency,
  });

  return (
    <Page title={t("business.2c86ca")} sub={t("business.d9b01a")} testId="biz-acquiring-page">
      <div className="dash-grid dash-grid-2">
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.f610f4")}</h3>
          <div style={{ fontSize: 12, color: "var(--tg-text-dim)", marginBottom: 12 }}>{t("business.3e83d7")}<b style={{ color: "#10F4A7" }}>0.5%</b>{t("business.5bc3ba")}</div>
          <input className="auth-input" placeholder={t("business.f5bf2d")} value={amount} onChange={(e) => setAmount(e.target.value)} type="number" data-testid="acq-amount" style={{ marginBottom: 10 }} />
          <input className="auth-input" placeholder={t("business.d5ad50")} value={terminal} onChange={(e) => setTerminal(e.target.value)} data-testid="acq-terminal" style={{ marginBottom: 14 }} />
          <button className="tg-btn primary" onClick={gen} data-testid="acq-generate">{t("business.f9f321")}</button>
        </Card>

        <Card style={{ textAlign: "center" }}>
          {qr ? (
            <>
              <img src={qr.dataUrl} alt="QR invoice" style={{ width: 200, height: 200, margin: "0 auto 14px", borderRadius: 14, display: "block", background: "#fff", padding: 8 }} data-testid="acq-qr-img" />
              <div style={{ fontSize: 12, color: "var(--tg-text-dim)" }}>{t("business.a5e577")}{qr.id}</div>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#FFD700", margin: "6px 0" }}>€{qr.amount}</div>
              <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginBottom: 14 }}>{qr.terminal} · {qr.created}</div>
              <button
                className="tg-btn gold"
                onClick={() => setModalOpen(true)}
                data-testid="acq-download-pdf"
                style={{ padding: "10px 16px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <Download size={14} />{t("business.b93248")}</button>
            </>
          ) : (
            <div style={{ color: "var(--tg-text-hint)", fontSize: 13 }}>
              <QrCode size={48} style={{ opacity: 0.3, marginBottom: 10 }} /><br />{t("business.30e52f")}</div>
          )}
        </Card>
      </div>

      {modalOpen && qr && (
        <PDFExportModal
          onClose={() => setModalOpen(false)}
          onConfirm={downloadPDF}
          title={t("business.93b056")}
        />
      )}

      <Card style={{ marginTop: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{t("business.644493")}{terminals.length})</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <input className="auth-input" placeholder={t("business.602680")} value={newTerm.name} onChange={(e) => setNewTerm({ ...newTerm, name: e.target.value })} style={{ width: 130, padding: "6px 10px", fontSize: 12 }} data-testid="term-name" />
            <input className="auth-input" placeholder={t("business.fb0034")} value={newTerm.location} onChange={(e) => setNewTerm({ ...newTerm, location: e.target.value })} style={{ width: 180, padding: "6px 10px", fontSize: 12 }} data-testid="term-location" />
            <select className="auth-input" value={newTerm.type} onChange={(e) => setNewTerm({ ...newTerm, type: e.target.value })} style={{ width: 110, padding: "6px 10px", fontSize: 12 }}>
              <option>QR + NFC</option><option>QR only</option><option>Web SDK</option>
            </select>
            <button className="tg-btn primary" onClick={addTerminal} data-testid="term-add" style={{ padding: "6px 14px", fontSize: 12 }}><Plus size={12} /></button>
          </div>
        </div>
        {terminals.length === 0 ? (
          <p style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: 20 }}>{t("business.2cc11c")}</p>
        ) : (
          <table className="dash-table">
            <thead><tr><th>ID</th><th>{t("business.602680")}</th><th>{t("business.fb0034")}</th><th>{t("pages3.345805")}</th><th>{t("pages.7203f7")}</th><th></th></tr></thead>
            <tbody>
              {terminals.map((t) => (
                <tr key={t.id} data-testid={`term-row-${t.id}`}>
                  <td style={{ fontFamily: "monospace", color: "var(--tg-blue)", fontSize: 11 }}>{t.id}</td>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td style={{ color: "var(--tg-text-dim)" }}>{t.location || "—"}</td>
                  <td>{t.type}</td>
                  <td><span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, background: "rgba(16,244,167,0.1)", color: "#10F4A7" }}>● {t.status}</span></td>
                  <td><button className="dash-icon-btn" onClick={() => delTerminal(t.id)} data-testid={`term-del-${t.id}`} style={{ color: "#FF3D6C" }}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </Page>
  );
}

/* ═══════════════════ 3. SWIFT B2B ═══════════════════ */
export function SwiftBizPage() {
  const { t } = useT();
  const [form, setForm] = useState({ beneficiary: "", iban: "", swift: "", amount: "", currency: "EUR", purpose: "" });
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const send = () => {
    if (!form.iban || !form.amount) return;
    const ref = "SWIFT-B2B-" + Math.random().toString(36).slice(2, 10).toUpperCase();
    setResult({ ref, ...form, eta: t("business.794e5f"), fee: (Number(form.amount) * 0.005).toFixed(2), sent: new Date().toLocaleString("ru-RU") });
  };
  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const downloadPDF = (lang, currency) => generateReceiptPDF({
    type: "SWIFT-B2B",
    reference: result.ref,
    rows: [
      ["Beneficiary",    result.beneficiary || "—"],
      ["IBAN / Account", result.iban],
      ["SWIFT / BIC",    result.swift || "—"],
      ["Amount",         `${result.amount} ${result.currency}`],
      ["Fee (Турбо-SWIFT B2B: 0.5%)",     `€${result.fee}`],
      ["Purpose",        result.purpose || "—"],
      ["ETA",            result.eta],
      ["Sent at",        result.sent],
    ],
    lang, currency,
  });

  return (
    <Page title="🌐 SWIFT B2B" sub={t("business.90574c")} testId="biz-swift-page">
      <div className="dash-grid dash-grid-2">
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.107be6")}</h3>
          <input className="auth-input" placeholder={t("business.525d8d")}   value={form.beneficiary} onChange={f("beneficiary")} style={{ marginBottom: 10 }} data-testid="swift-beneficiary" />
          <input className="auth-input" placeholder="IBAN / Account Number" value={form.iban}        onChange={f("iban")}        style={{ marginBottom: 10 }} data-testid="swift-iban" />
          <input className="auth-input" placeholder="SWIFT / BIC Code"       value={form.swift}       onChange={f("swift")}       style={{ marginBottom: 10 }} data-testid="swift-bic" />
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <input className="auth-input" placeholder={t("pages3.cf59eb")}                 value={form.amount}    onChange={f("amount")}    type="number" style={{ flex: 2 }} data-testid="swift-amount" />
            <select className="auth-input" value={form.currency} onChange={f("currency")} style={{ flex: 1 }}>
              <option>EUR</option><option>USD</option><option>GBP</option><option>USDT</option>
            </select>
          </div>
          <input className="auth-input" placeholder={t("business.711a37")}       value={form.purpose}    onChange={f("purpose")}    style={{ marginBottom: 14 }} />
          <div data-testid="biz-tariff-strip"
            style={{ display:"flex", gap: 8, flexWrap:"wrap", marginBottom: 14,
                     padding: 10, borderRadius: 10,
                     background:"rgba(16,244,167,0.06)", border:"1px solid rgba(16,244,167,0.35)" }}>
            <span style={{ fontSize:11, fontWeight:800, color:"#10F4A7", letterSpacing:1 }}>Турбо-Эквайринг: 0.4%</span>
            <span style={{ fontSize:11, color:"#8B94B8" }}>·</span>
            <span style={{ fontSize:11, fontWeight:800, color:"#10F4A7", letterSpacing:1 }}>Турбо-SWIFT B2B: 0.5%</span>
            <span style={{ fontSize:11, color:"#8B94B8" }}>·</span>
            <span style={{ fontSize:11, fontWeight:800, color:"#FFD700", letterSpacing:1 }}>Скорость расчёта: 0.15 секунды</span>
          </div>
          <button className="tg-btn primary" onClick={send} data-testid="swift-send">{t("business.18ffb7")}</button>
        </Card>

        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.2c816f")}</h3>
          {result ? (
            <div style={{ fontSize: 13, lineHeight: 2 }}>
              <div><span style={{ color: "var(--tg-text-dim)" }}>Reference:</span> <b style={{ color: "var(--tg-blue)", fontFamily: "monospace" }}>{result.ref}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>{t("business.54d33d")}</span> <b>{result.beneficiary || "—"}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>IBAN:</span> <b>{result.iban}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>{t("business.19fcef")}</span> <b style={{ color: "#FFD700" }}>{result.amount} {result.currency}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>Комиссия (Турбо-SWIFT B2B: 0.5%):</span> <b style={{ color: "#10F4A7" }}>€{result.fee}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>Расчёт:</span> <b style={{ color: "#FFD700" }}>0.15 секунды</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>ETA:</span> <b>{result.eta}</b></div>
              <div style={{ marginTop: 14, padding: 12, background: "rgba(16,244,167,0.08)", borderRadius: 10, color: "#10F4A7", fontSize: 12 }}>{t("business.8ca433")}</div>
              <button
                className="tg-btn gold"
                data-testid="swift-download-pdf"
                onClick={() => setModalOpen(true)}
                style={{ marginTop: 14, padding: "10px 16px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <Download size={14} />{t("business.b3a3ad")}</button>
            </div>
          ) : (
            <div style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: "40px 0" }}>
              <Send size={40} style={{ opacity: 0.3, marginBottom: 10 }} /><br />{t("business.18db2f")}</div>
          )}
        </Card>
      </div>

      <Card style={{ marginTop: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.a7e017")}</h3>
        <table className="dash-table">
          <thead><tr><th>Ref</th><th>{t("user.e3956f")}</th><th>{t("pages3.cf59eb")}</th><th>{t("pages.400a03")}</th><th>{t("pages2.8cdd8b")}</th><th>{t("pages.7203f7")}</th></tr></thead>
          <tbody>
            {[
              ["SWIFT-B2B-8A2F1C", t("business.b08116"), "12 000 EUR", "36.00",  t("business.950e6d"), "✓ settled"],
              ["SWIFT-B2B-73BC92", "Apex Supplies Ltd.",    "45 000 USD", "135.00", t("business.01c34f"), "✓ settled"],
              ["SWIFT-B2B-1E44AA", "GreenFactor SA",         "8 500 EUR",  "25.50",  t("business.48f012"), "✓ settled"],
            ].map(([r, b, a, f2, d, s]) => (
              <tr key={r}>
                <td style={{ fontFamily: "monospace", color: "var(--tg-blue)" }}>{r}</td>
                <td>{b}</td>
                <td style={{ fontWeight: 700 }}>{a}</td>
                <td>€{f2}</td>
                <td style={{ color: "var(--tg-text-dim)" }}>{d}</td>
                <td style={{ color: "#10F4A7" }}>{s}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {modalOpen && result && (
        <PDFExportModal
          onClose={() => setModalOpen(false)}
          onConfirm={downloadPDF}
          title={t("business.7bde1e")}
        />
      )}
    </Page>
  );
}

/* ═══════════════════ 4. ANALYTICS ═══════════════════ */
export function AnalyticsPage() {
  const { t } = useT();
  const [stats, setStats] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  useEffect(() => { api.get("/business/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const doExport = async (days, format, lang) => {
    try {
      const r = await api.get(`/business/report?days=${days}`);
      if (format === "csv") exportReportCSV(r.data, lang);
      else                  await exportReportPDF(r.data, lang);
    } catch (e) {
      console.error("Export failed:", e);
      alert(t("business.4409d2"));
    }
  };

  return (
    <Page title={t("business.9e7c27")} sub={t("business.9f207b")} testId="biz-analytics-page">
      <div className="dash-grid dash-grid-3" style={{ marginBottom: 22 }}>
        {[
          [t("business.96269c"),                stats ? `€${fmt(stats.todayRevenue)}` : "—", "#00F2FF"],
          [t("business.be3b44"),                 stats ? `€${fmt(stats.weekRevenue)}` : "—",  "#10F4A7"],
          ["Refund Rate",            stats ? `${fmt(stats.refundRate)}%` : "—",   "#FFD700"],
        ].map(([l, v, c], i) => (
          <Card key={i} style={{ borderColor: `${c}33` }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: c }}>{l}</div>
            <div style={{ fontSize: 30, fontWeight: 900, marginTop: 6 }}>{v}</div>
          </Card>
        ))}
      </div>

      {/* Top Customers */}
      <Card style={{ marginBottom: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.fff776")}</h3>
        <table className="dash-table">
          <thead><tr><th>#</th><th>{t("business.4af22f")}</th><th>{t("user.8a02e2")}</th><th>{t("business.090552")}</th><th>{t("business.66e417")}</th></tr></thead>
          <tbody>
            {(stats?.topCustomers || []).map((c, i) => (
              <tr key={i}>
                <td style={{ color: "var(--tg-blue)", fontWeight: 700 }}>#{i + 1}</td>
                <td style={{ fontWeight: 600 }}>{c.name}</td>
                <td style={{ fontSize: 16 }}>{c.country}</td>
                <td>{c.orders}</td>
                <td style={{ fontWeight: 700, color: "#FFD700" }}>€{fmt(c.spent)}</td>
              </tr>
            ))}
            {!stats && <tr><td colSpan={5} style={{ color: "var(--tg-text-hint)", textAlign: "center" }}>{t("user.89d69a")}</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card style={{ marginBottom: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.de1987")}</h3>
        {[
          ["QR Code",    62, "#00F2FF"],
          ["Web SDK",    24, "#FFD700"],
          ["NFC",        9,  "#10F4A7"],
          ["API",        5,  "#BC13FE"],
        ].map(([l, p, c]) => (
          <div key={l} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span>{l}</span><b style={{ color: c }}>{p}%</b>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,0.04)" }}>
              <div style={{ width: `${p}%`, height: "100%", background: c, borderRadius: 4 }} />
            </div>
          </div>
        ))}
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{t("business.5304c4")}</h3>
          <span style={{ fontSize: 11, color: "var(--tg-text-hint)" }}>{t("business.bd02ad")}</span>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="tg-btn gold"
            onClick={() => setExportOpen(true)}
            data-testid="report-export-open"
            style={{ padding: "10px 18px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Download size={14} />{t("business.df9db0")}</button>
          <div style={{ fontSize: 11, color: "var(--tg-text-hint)", alignSelf: "center" }}>{t("business.ae5e15")}</div>
        </div>
      </Card>

      {exportOpen && (
        <ReportExportModal onClose={() => setExportOpen(false)} onConfirm={doExport} />
      )}
    </Page>
  );
}

/* ═══════════════════ 5. API & SDK ═══════════════════ */
export function ApiPage() {
  const { t } = useT();
  const [showKey, setShowKey] = useState({});  // {keyId: bool}
  const [copied, setCopied] = useState(null);
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [newWh, setNewWh] = useState({ event: "charge.succeeded", url: "" });

  const loadAll = async () => {
    try {
      const [k, w] = await Promise.all([api.get("/biz/apikeys"), api.get("/biz/webhooks")]);
      setKeys(k.data.keys || []); setWebhooks(w.data.webhooks || []);
    } catch (e) {}
  };
  useEffect(() => { loadAll(); }, []);

  const createKey = async () => {
    try { await api.post("/biz/apikeys"); loadAll(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const revokeKey = async (id) => {
    if (!window.confirm(`${t("business.confirm-revoke")} ${id}? ${t("business.action-irreversible")}`)) return;
    try { await api.delete(`/biz/apikeys/${id}`); loadAll(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const addWebhook = async () => {
    if (!newWh.url) return;
    try {
      await api.post("/biz/webhooks", newWh);
      setNewWh({ event: "charge.succeeded", url: "" }); loadAll();
    } catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const toggleWh = async (id) => {
    try { await api.patch(`/biz/webhooks/${id}/toggle`); loadAll(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const delWh = async (id) => {
    try { await api.delete(`/biz/webhooks/${id}`); loadAll(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const copy = (t, id) => { navigator.clipboard.writeText(t); setCopied(id); setTimeout(() => setCopied(null), 1500); };
  const sample = keys[0]?.secret || "sk_live_•••";

  return (
    <Page title="🔑 API & SDK" sub={t("business.da5c29")} testId="biz-api-page">
      {/* Keys list */}
      <Card style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{t("business.bf5896")}{keys.length})</h3>
          <button className="tg-btn primary" onClick={createKey} data-testid="apikey-create" style={{ padding: "6px 14px", fontSize: 12 }}><Plus size={12} />{t("business.2d61b3")}</button>
        </div>
        {keys.length === 0 ? (
          <p style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: 16 }}>{t("business.07a322")}</p>
        ) : keys.map((k) => (
          <div key={k.id} data-testid={`apikey-row-${k.id}`} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>{k.id} · {t("business.created")} {k.createdAt?.slice(0, 10)}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", background: "rgba(255,255,255,0.03)", padding: "10px 12px", borderRadius: 10, border: "1px solid var(--tg-border)" }}>
              <code style={{ flex: 1, fontFamily: "monospace", fontSize: 13, color: "#FFD700" }}>
                {showKey[k.id] ? k.secret : k.secret.replace(/./g, "•").slice(0, 38)}
              </code>
              <button className="dash-icon-btn" onClick={() => setShowKey((s) => ({ ...s, [k.id]: !s[k.id] }))} data-testid={`apikey-toggle-${k.id}`}>{showKey[k.id] ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              <button className="dash-icon-btn" onClick={() => copy(k.secret, k.id)} data-testid={`apikey-copy-${k.id}`}>{copied === k.id ? <Check size={14} /> : <Copy size={14} />}</button>
              <button className="dash-icon-btn" onClick={() => revokeKey(k.id)} data-testid={`apikey-revoke-${k.id}`} style={{ color: "#FF3D6C" }}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}

        <div style={{ padding: 12, background: "rgba(255,61,108,0.08)", border: "1px solid rgba(255,61,108,0.3)", borderRadius: 10, fontSize: 12, color: "#FF8FA6", marginTop: 12 }}>
          <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{t("business.18cb66")}</div>
      </Card>

      {/* Code samples */}
      <Card style={{ marginBottom: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.9928d6")}</h3>
        <pre style={{ background: "#0b0b10", padding: 16, borderRadius: 10, fontSize: 12, color: "#E6E6E6", overflow: "auto", margin: 0, border: "1px solid var(--tg-border)" }}>
{`curl -X POST https://api.turbo.global/v1/charges \\
  -H "Authorization: Bearer ${sample}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 99.00,
    "currency": "EUR",
    "description": "Order #1234",
    "webhook_url": "https://your-shop.com/webhook"
  }'`}
        </pre>
      </Card>

      {/* Webhooks */}
      <Card style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Webhook Endpoints ({webhooks.length})</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <select className="auth-input" value={newWh.event} onChange={(e) => setNewWh({ ...newWh, event: e.target.value })} style={{ width: 170, padding: "6px 10px", fontSize: 12 }} data-testid="webhook-event">
              <option>charge.succeeded</option><option>charge.failed</option><option>payout.completed</option><option>refund.created</option>
            </select>
            <input className="auth-input" placeholder="https://your-shop.com/hook" value={newWh.url} onChange={(e) => setNewWh({ ...newWh, url: e.target.value })} style={{ width: 280, padding: "6px 10px", fontSize: 12 }} data-testid="webhook-url" />
            <button className="tg-btn primary" onClick={addWebhook} data-testid="webhook-add" style={{ padding: "6px 14px", fontSize: 12 }}><Plus size={12} /></button>
          </div>
        </div>
        {webhooks.length === 0 ? (
          <p style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: 16 }}>{t("business.544415")}</p>
        ) : (
          <table className="dash-table">
            <thead><tr><th>Event</th><th>URL</th><th>{t("pages.7203f7")}</th><th></th></tr></thead>
            <tbody>
              {webhooks.map((w) => (
                <tr key={w.id} data-testid={`webhook-row-${w.id}`}>
                  <td><code style={{ color: "var(--tg-blue)", fontSize: 12 }}>{w.event}</code></td>
                  <td style={{ fontFamily: "monospace", fontSize: 11, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.url}</td>
                  <td>
                    <button onClick={() => toggleWh(w.id)} data-testid={`webhook-toggle-${w.id}`} style={{ background: "transparent", border: "none", cursor: "pointer", color: w.status === "active" ? "#10F4A7" : "#FFD700", fontWeight: 700, fontSize: 12 }}>
                      ● {w.status}
                    </button>
                  </td>
                  <td><button className="dash-icon-btn" onClick={() => delWh(w.id)} data-testid={`webhook-del-${w.id}`} style={{ color: "#FF3D6C" }}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.c63d29")}</h3>
        <div className="dash-grid dash-grid-3">
          {[
            ["Node.js",    "npm i @turbo-global/sdk"],
            ["Python",     "pip install turbo-global"],
            ["PHP",        "composer require turbo/sdk"],
            ["React",      "npm i @turbo-global/react"],
            ["WordPress",  "Plugin · TGC Checkout"],
            ["Shopify",    "App · TGC Payments"],
          ].map(([lang, cmd]) => (
            <div key={lang} className="tg-card" style={{ padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{lang}</div>
              <code style={{ fontSize: 11, color: "var(--tg-text-dim)", display: "block", marginTop: 6, fontFamily: "monospace" }}>{cmd}</code>
              <a href="#" style={{ fontSize: 11, color: "var(--tg-blue)", marginTop: 8, display: "inline-block" }}><ExternalLink size={11} />{t("business.3d3003")}</a>
            </div>
          ))}
        </div>
      </Card>
    </Page>
  );
}

/* ═══════════════════ 6. CATALOG ═══════════════════ */
export function CatalogPage() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: "", priceEur: "" });

  const load = async () => {
    try { const r = await api.get("/biz/catalog"); setItems(r.data.items || []); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newItem.name || !newItem.priceEur) return;
    try {
      await api.post("/biz/catalog", { name: newItem.name, priceEur: Number(newItem.priceEur) });
      setNewItem({ name: "", priceEur: "" }); load();
    } catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const del = async (id) => {
    if (!window.confirm(`${t("business.confirm-delete")} ${id}?`)) return;
    try { await api.delete(`/biz/catalog/${id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const toggle = async (id) => {
    try { await api.patch(`/biz/catalog/${id}/toggle`); load(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };

  return (
    <Page title={t("business.8cb088")} sub={`${t("business.active-count")}: ${items.filter((i) => i.active).length} ${t("business.of")} ${items.length}`} testId="biz-catalog-page">
      <Card style={{ marginBottom: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.697c0f")}</h3>
        <div style={{ display: "flex", gap: 10 }}>
          <input className="auth-input" placeholder={t("business.602680")}         value={newItem.name}     onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}     style={{ flex: 3 }} data-testid="catalog-name" />
          <input className="auth-input" placeholder={t("business.73740e")} type="number" value={newItem.priceEur} onChange={(e) => setNewItem({ ...newItem, priceEur: e.target.value })} style={{ flex: 1 }} data-testid="catalog-price" />
          <button className="tg-btn primary" onClick={add} data-testid="catalog-add"><Plus size={14} />{t("business.5eba28")}</button>
        </div>
      </Card>

      <Card>
        {items.length === 0 ? (
          <p style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: 20 }}>{t("business.0d93e5")}</p>
        ) : (
          <table className="dash-table">
            <thead><tr><th>ID</th><th>{t("business.602680")}</th><th>{t("functional.682fa8")}</th><th>TGC</th><th>{t("pages.7203f7")}</th><th></th></tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`catalog-row-${p.id}`}>
                  <td style={{ fontFamily: "monospace", color: "var(--tg-blue)", fontSize: 11 }}>{p.id}</td>
                  <td style={{ fontWeight: 600 }}>{p.name}</td>
                  <td style={{ fontWeight: 700, color: "#FFD700" }}>€{fmt(p.priceEur)}</td>
                  <td>{fmt(p.priceTGC)}</td>
                  <td>
                    <button onClick={() => toggle(p.id)} data-testid={`catalog-toggle-${p.id}`} style={{ background: "transparent", border: "none", cursor: "pointer", color: p.active ? "#10F4A7" : "#666", fontWeight: 700, fontSize: 12 }}>
                      {p.active ? t("business.1906bb") : t("business.b5603f")}
                    </button>
                  </td>
                  <td><button className="dash-icon-btn" onClick={() => del(p.id)} data-testid={`catalog-del-${p.id}`} style={{ color: "#FF3D6C" }}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </Page>
  );
}

/* ═══════════════════ 7. TEAM ═══════════════════ */
export function TeamPage() {
  const { t } = useT();
  const [team, setTeam] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "Finance" });
  const load = async () => {
    try { const r = await api.get("/biz/team"); setTeam(r.data.team || []); } catch (e) {}
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!invite.email) return;
    try {
      await api.post("/biz/team", invite);
      setInvite({ email: "", role: "Finance" }); load();
    } catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("business.2eb380"))) return;
    try { await api.delete(`/biz/team/${id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };

  return (
    <Page title={t("business.04ccd6")} sub={t("business.811bda")} testId="biz-team-page">
      <Card style={{ marginBottom: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.05ed58")}</h3>
        <div style={{ display: "flex", gap: 10 }}>
          <input className="auth-input" placeholder="email@company.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} style={{ flex: 3 }} data-testid="team-email" />
          <select className="auth-input" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} style={{ flex: 1 }} data-testid="team-role">
            <option>Owner</option><option>Finance</option><option>Support</option><option>Developer</option><option>Viewer</option>
          </select>
          <button className="tg-btn primary" onClick={add} data-testid="team-invite"><Plus size={14} />{t("business.5e134c")}</button>
        </div>
      </Card>

      <Card>
        {team.length === 0 ? (
          <p style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: 16 }}>{t("business.d2e14c")}</p>
        ) : (
          <table className="dash-table">
            <thead><tr><th>Email</th><th>{t("business.5d621b")}</th><th>{t("pages.7203f7")}</th><th></th></tr></thead>
            <tbody>
              {team.map((t) => (
                <tr key={t.id} data-testid={`team-row-${t.id}`}>
                  <td style={{ fontWeight: 600 }}>{t.email}</td>
                  <td><span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, background: "rgba(0,242,255,0.08)", color: "var(--tg-blue)", fontWeight: 700 }}>{t.role}</span></td>
                  <td style={{ color: t.status === "active" ? "#10F4A7" : "#FFD700" }}>● {t.status === "active" ? t("business.status-active") : t("business.status-pending")}</td>
                  <td>{t.role !== "Owner" && <button className="dash-icon-btn" onClick={() => remove(t.id)} data-testid={`team-del-${t.id}`} style={{ color: "#FF3D6C" }}><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card style={{ marginTop: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.26cd4b")}</h3>
        <table className="dash-table">
          <thead><tr><th>{t("business.1b05f8")}</th><th>Owner</th><th>Finance</th><th>Support</th><th>Developer</th><th>Viewer</th></tr></thead>
          <tbody>
            {[
              [t("business.a58764"),   "✓","✓","read","read","read"],
              ["SWIFT B2B",   "✓","✓","—","—","—"],
              [t("business.d6f405"),   "✓","—","—","✓","—"],
              [t("business.ad5122"),     "✓","read","✓","—","read"],
              [t("pages.404752"),   "✓","✓","read","read","read"],
              [t("business.f737f8"),"✓","✓","—","—","—"],
            ].map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j} style={{ color: c === "✓" ? "#10F4A7" : c === "—" ? "#666" : "var(--tg-text-dim)", fontWeight: c === "✓" ? 700 : 400 }}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ═══════════════════ 8. WITHDRAW ═══════════════════ */
export function WithdrawPage() {
  const { t } = useT();
  const [form, setForm] = useState({ amount: "", method: "SEPA", iban: "" });
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const withdraw = () => {
    if (!form.amount) return;
    const ref = "WDR-" + Math.random().toString(36).slice(2, 10).toUpperCase();
    setResult({ ref, ...form, eta: t("business.794e5f"), fee: (Number(form.amount) * 0.005).toFixed(2) });
  };
  const downloadPDF = (lang, currency) => generateReceiptPDF({
    type: "WITHDRAW",
    reference: result.ref,
    rows: [
      ["Amount (TGC)",   `${fmt(result.amount)} TGC`],
      ["Amount (EUR)",   `€${fmt(result.amount * 0.10)}`],
      ["Fee (Турбо-SWIFT B2B: 0.5%)",     `€${result.fee}`],
      ["Method",         result.method],
      ["Destination",    result.iban || "—"],
      ["ETA",            result.eta],
      ["Initiated at",   new Date().toLocaleString("en-GB")],
    ],
    lang, currency,
  });

  return (
    <Page title={t("business.be961b")} sub={t("business.060585")} testId="biz-withdraw-page">
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr 1fr", gap: 18, alignItems: "start" }} data-testid="withdraw-grid">
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.1fe1de")}</h3>
          <input className="auth-input" placeholder={t("functional.2f987a")} type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ marginBottom: 10 }} data-testid="wd-amount" />
          <select className="auth-input" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} style={{ marginBottom: 10 }}>
            <option>SEPA (EUR)</option><option>SWIFT (USD)</option><option>USDT (TRC-20)</option><option>USDC (ERC-20)</option>
          </select>
          <input className="auth-input" placeholder="IBAN / Wallet Address" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} style={{ marginBottom: 14 }} data-testid="wd-iban" />
          <button className="tg-btn primary" onClick={withdraw} data-testid="wd-send">{t("business.4c072c")}</button>
          <div style={{ marginTop: 14, padding: 12, background: "rgba(16,244,167,0.08)", borderRadius: 10, color: "#10F4A7", fontSize: 12 }}>{t("business.978637")}</div>
        </Card>

        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.2c816f")}</h3>
          {result ? (
            <div style={{ fontSize: 13, lineHeight: 2 }}>
              <div><span style={{ color: "var(--tg-text-dim)" }}>Reference:</span> <b style={{ color: "var(--tg-blue)", fontFamily: "monospace" }}>{result.ref}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>{t("business.19fcef")}</span> <b style={{ color: "#FFD700" }}>{fmt(result.amount)} TGC → €{fmt(result.amount * 0.10)}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>{t("business.3e83d7")}</span> <b>€{result.fee}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>{t("business.ce8740")}</span> <b>{result.method}</b></div>
              <div><span style={{ color: "var(--tg-text-dim)" }}>ETA:</span> <b>{result.eta}</b></div>
              <div style={{ marginTop: 14, padding: 12, background: "rgba(16,244,167,0.08)", borderRadius: 10, color: "#10F4A7", fontSize: 12 }}>{t("business.06a766")}</div>
              <button
                className="tg-btn gold"
                data-testid="wd-download-pdf"
                onClick={() => setModalOpen(true)}
                style={{ marginTop: 14, padding: "10px 16px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <Download size={14} />{t("business.b3a3ad")}</button>
            </div>
          ) : (
            <div style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: "40px 0" }}>
              <Landmark size={40} style={{ opacity: 0.3, marginBottom: 10 }} /><br />{t("business.1d6eec")}</div>
          )}
        </Card>
        <AntiSanctionSidebar />
      </div>

      <Card style={{ marginTop: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>{t("business.c981af")}</h3>
        <table className="dash-table">
          <thead><tr><th>Ref</th><th>{t("pages3.cf59eb")}</th><th>{t("business.c8f1e8")}</th><th>{t("pages2.8cdd8b")}</th><th>{t("pages.7203f7")}</th></tr></thead>
          <tbody>
            {[
              ["WDR-9A82F1", "50 000 TGC / €5 000",   "SEPA",       t("business.7f4ce0"), "✓ completed"],
              ["WDR-47BC92", "120 000 TGC / €12 000", "SWIFT",      t("business.dce1b6"), "✓ completed"],
              ["WDR-1E44AA", "25 000 TGC / €2 500",   "USDT TRC-20",t("business.c1cf25"), "✓ completed"],
            ].map(([r, a, m, d, s]) => (
              <tr key={r}>
                <td style={{ fontFamily: "monospace", color: "var(--tg-blue)" }}>{r}</td>
                <td style={{ fontWeight: 700 }}>{a}</td>
                <td>{m}</td>
                <td style={{ color: "var(--tg-text-dim)" }}>{d}</td>
                <td style={{ color: "#10F4A7" }}>{s}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {modalOpen && result && (
        <PDFExportModal
          onClose={() => setModalOpen(false)}
          onConfirm={downloadPDF}
          title={t("business.753f1e")}
        />
      )}
    </Page>
  );
}

/* ═══════════════════ 9. DOCS ═══════════════════ */
export function DocsPage() {
  const { t } = useT();
  return (
    <Page title={t("business.63d6a3")} sub={t("business.ddbb2d")} testId="biz-docs-page">
      <div className="dash-grid dash-grid-2">
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 15 }}><FileText size={16} style={{ verticalAlign: "middle" }} />{t("business.bc7343")}</h3>
          {[
            ["Merchant Agreement",         t("business.90437a"),   t("business.98f20d")],
            ["SLA: Payments 99.95%",       t("business.e7546d"),    t("business.f16088")],
            ["AML / KYC Compliance Policy",t("business.a4523e"),    t("business.98f20d")],
            ["Data Processing Addendum",   t("business.719b2b"),    t("business.98f20d")],
          ].map(([t, m, s]) => (
            <div key={t} style={{ padding: "12px 0", borderBottom: "1px dashed var(--tg-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{t}</div>
                <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 2 }}>{m} · <span style={{ color: "#10F4A7" }}>{s}</span></div>
              </div>
              <button className="dash-icon-btn"><Download size={14} /></button>
            </div>
          ))}
        </Card>

        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 15 }}><Banknote size={16} style={{ verticalAlign: "middle" }} />{t("business.430703")}</h3>
          {[
            [t("business.0e2717"),   t("business.2ddfb0"), t("business.ee6912")],
            [t("business.1c2b2c"),      t("business.eeba78"),  t("business.9f68ed")],
            [t("business.63adad"),t("business.186340"),  t("business.8cd28a")],
            [t("business.f8a35b"),       t("business.8bfa42"), t("business.f4d928")],
          ].map(([t, m, s]) => (
            <div key={t} style={{ padding: "12px 0", borderBottom: "1px dashed var(--tg-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{t}</div>
                <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 2 }}>{m} · <span style={{ color: "#FFD700" }}>{s}</span></div>
              </div>
              <button className="dash-icon-btn"><Download size={14} /></button>
            </div>
          ))}
        </Card>
      </div>

      <Card style={{ marginTop: 22 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}><Shield size={16} style={{ verticalAlign: "middle" }} />{t("business.3bf386")}</h3>
        <div className="dash-grid dash-grid-3">
          {[
            ["HK VASP",      t("business.6cf6ca"),  "Q2 2026", "#FFD700"],
            ["AIFC AFSA",    t("business.6cf6ca"),  "Q2 2026", "#FFD700"],
            ["ISO 27001",    t("business.270fd5"),     t("business.5fc7dc"), "#10F4A7"],
            ["SOC 2 Type II",t("business.270fd5"),     t("business.2fdc32"), "#10F4A7"],
            ["PCI DSS L1",   t("business.270fd5"),     t("business.2fdc32"), "#10F4A7"],
            ["GDPR DPA",     t("business.8cd28a"),    t("business.c7da93"),"#10F4A7"],
          ].map(([t, s, e, c]) => (
            <div key={t} className="tg-card" style={{ padding: 14, borderColor: `${c}33` }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{t}</div>
              <div style={{ fontSize: 11, color: c, marginTop: 4 }}>● {s}</div>
              <div style={{ fontSize: 10, color: "var(--tg-text-hint)", marginTop: 2 }}>{e}</div>
            </div>
          ))}
        </div>
      </Card>
    </Page>
  );
}
